import { FC, useState, useEffect, useRef } from 'react';
import FilterDropdown, { FilterOption } from '../custom-input/FilterDropdown';
import ValidatedInput from '../custom-input/ValidatedInput';
import { TransactionFilterOption } from '../transaction/TransactionsList';
import { SearchSVG } from '../svgs';
import { LABEL_CONSTANTS } from '../../constants/common';
import './searchBy.scss';

export type SearchByItem = { id?: string; criteria: FilterOption<any>; query: string };

interface SearchByProps {
  searches: SearchByItem[];
  setSearches: (e: SearchByItem[]) => void;
  searchCriteriaOptions: FilterOption<any>[];
  resetSearchCriteria?: number;
  removedFilter?: { criteria: FilterOption<TransactionFilterOption>; query: string };
}

const SEARCH_DEBOUNCE_MS = 400;

const SearchBy: FC<SearchByProps> = ({ searchCriteriaOptions, searches, setSearches, resetSearchCriteria, removedFilter }) => {
  const [selectedSearchCriteria, setSelectedSearchCriteria] = useState<FilterOption<any> | null>(null);
  const [query, setQuery] = useState<string>('');
  const activeSearchIdRef = useRef<string | null>(null);
  const nextSearchIdRef = useRef(1);
  const searchesRef = useRef(searches);
  searchesRef.current = searches;

  const buildUpdatedSearches = (
    criteria: FilterOption<any>,
    nextQuery: string,
    currentSearches: SearchByItem[],
  ): SearchByItem[] | null => {
    const updatedSearches = [...currentSearches];
    const activeId = activeSearchIdRef.current;
    const existingIndex = activeId ? updatedSearches.findIndex(item => item.id === activeId) : -1;

    if (nextQuery.trim()) {
      if (existingIndex !== -1) {
        updatedSearches[existingIndex] = { ...updatedSearches[existingIndex], criteria, query: nextQuery };
      } else {
        const id = `search-${nextSearchIdRef.current++}`;
        activeSearchIdRef.current = id;
        updatedSearches.push({ id, criteria, query: nextQuery });
      }
    } else if (existingIndex !== -1) {
      updatedSearches.splice(existingIndex, 1);
      activeSearchIdRef.current = null;
    } else {
      return null;
    }

    return updatedSearches;
  };

  const handleSearchCriteriaChange = (newCriteria: FilterOption<any>) => {
    setSelectedSearchCriteria(newCriteria);
    activeSearchIdRef.current = null;
    setQuery('');
  };

  useEffect(() => {
    if (!selectedSearchCriteria) return;

    const delay = query.trim() ? SEARCH_DEBOUNCE_MS : 0;
    const timeoutId = window.setTimeout(() => {
      const updatedSearches = buildUpdatedSearches(selectedSearchCriteria, query, searchesRef.current);
      if (updatedSearches) {
        setSearches(updatedSearches);
      }
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [selectedSearchCriteria, query]);

  useEffect(() => {
    if (searches.length === 0 || (removedFilter && removedFilter.criteria.name === selectedSearchCriteria?.name)) {
      setSelectedSearchCriteria(null);
      setQuery('');
      activeSearchIdRef.current = null;
    }
  }, [resetSearchCriteria, removedFilter]);

  return (
    <>
      <div className="search-by">
        <p className="search-by-label">{LABEL_CONSTANTS.SEARCH_BY}:</p>

        <div
          className="input-stack"
          onKeyDown={event => {
            if (event.key !== 'Enter' || !selectedSearchCriteria || !query.trim()) return;
            event.preventDefault();

            const updatedSearches = buildUpdatedSearches(selectedSearchCriteria, query, searchesRef.current);
            if (updatedSearches) {
              setSearches(updatedSearches);
            }

            activeSearchIdRef.current = null;
            setQuery('');
          }}>
          <FilterDropdown
            placeholder="Select"
            options={searchCriteriaOptions}
            value={{ name: selectedSearchCriteria?.name ?? '', value: selectedSearchCriteria?.value ?? '' }}
            onChange={handleSearchCriteriaChange}
          />

          <ValidatedInput
            disabled={!selectedSearchCriteria}
            validatedStateForAutoFill={{ value: query ?? '', valid: true }}
            placeholder={''}
            label={''}
            setValidatedState={e => setQuery(e.value)}
            validate={false}
            validators={[]}
          />
          <input disabled type="text" className="icon-holder" />
          <SearchSVG />
        </div>
      </div>
    </>
  );
};

export default SearchBy;
